/**
 * Geometry for the Fibonacci, extended-trendline and pitchfork drawing tools.
 *
 * ---------------------------------------------------------------------------------------------
 * X is a BAR INDEX, not a date.
 * ---------------------------------------------------------------------------------------------
 * The chart's x-axis is a category axis of date strings, so dates have no arithmetic: you cannot
 * subtract two of them to get a slope, and the gap between consecutive dates is not constant once
 * weekends and holidays are skipped. Every function here works in (barIndex, price) space, which
 * is uniform and where a slope actually means something, and the caller converts indices back to
 * dates when it builds the overlays.
 *
 * Using calendar days instead would tilt every line by however many non-trading days it happened
 * to span — visibly wrong on a long trendline, and subtly wrong on a short one.
 */

/**
 * Marks overlays that are construction geometry from these tools.
 *
 * Deliberately NOT the manual-draw source: a Fibonacci level or a pitchfork prong is a reference
 * line, not a measured move, so it must never pick up a gain badge or appear in the journal's
 * trendline summary as though someone had traded it.
 */
export const TECHNICAL_DRAW_SOURCE = 'technicalDraw';

/** The interior retracement ratios. */
export const FIB_RATIOS = [0.236, 0.382, 0.5, 0.618, 0.764];

/**
 * The 0% and 100% boundaries — the prices of the two clicked points themselves.
 *
 * Kept in the same level list as the interior ratios rather than drawn separately, because the
 * boundaries and the levels between them have to share an X-span exactly. Producing them from one
 * function is what guarantees that; two code paths computing "the same" span is how they end up a
 * bar apart.
 */
export const FIB_BOUNDARY_RATIOS = [0, 1];

/** Every ratio drawn, in price order from the first clicked point to the second. */
export const FIB_ALL_RATIOS = [...FIB_BOUNDARY_RATIOS, ...FIB_RATIOS].sort((a, b) => a - b);

const isNum = v => typeof v === 'number' && Number.isFinite(v);
const validPoint = p => !!p && isNum(p.index) && isNum(p.price);

/** Clamps an index into the chart, so nothing is ever emitted off the end of the axis. */
export function clampIndex(index, lastIndex) {
  if (!isNum(index) || !isNum(lastIndex)) return null;
  return Math.max(0, Math.min(lastIndex, Math.round(index)));
}

/**
 * Fibonacci retracement levels between two points.
 *
 * Y_level = Y1 + ratio * (Y2 - Y1), so the levels always run from P1 towards P2 regardless of
 * which is higher — drawing bottom-to-top and top-to-bottom give the same set of prices, just
 * labelled from the other end.
 *
 * @returns {{ratio:number, percentLabel:string, price:number}[]}
 */
export function fibonacciLevels(p1, p2) {
  if (!validPoint(p1) || !validPoint(p2)) return [];
  const span = p2.price - p1.price;
  return FIB_ALL_RATIOS.map(ratio => ({
    ratio,
    percentLabel: `${(ratio * 100).toFixed(1)}%`,
    /*
     * RETRACED FROM THE END, which is what a retracement IS — and the labels were inverted.
     *
     * It was `p1.price + ratio * span`, measuring forward from the START. That is an EXTENSION
     * scale, and on it the line labelled 23.6% sits 23.6% of the way up a rally — which is the
     * 76.4% retracement wearing the wrong name. Every interior label was its own complement:
     * 23.6 for 76.4, 38.2 for 61.8, and 50 correct only because it is its own mirror.
     *
     * <p>The symptom is what was reported: on a move drawn low to high the labels read 76.4 at
     * the top down to 23.6 at the bottom, when a shallow 23.6% pullback from the high belongs
     * at the TOP.
     *
     * <p>THE PRICES ARE UNCHANGED AS A SET. The same five interior lines are drawn at the same
     * five prices; each now carries the ratio that describes it. So nothing that reads a LEVEL
     * moves, and everything that reads a NAME is corrected.
     *
     * <p>The anchors swap ends with it, and that is the convention rather than a side effect:
     * 0% retraced is the end of the move and 100% retraced is its origin.
     */
    price: p2.price - ratio * span,
    // 0% and 100% are the anchors, drawn solid so the retracement's extent reads at a glance,
    // while the interior levels stay dotted. Note this is a ratio, not a screen position: which
    // of them is visually on top depends on whether the move was drawn upwards or downwards.
    isBoundary: FIB_BOUNDARY_RATIOS.includes(ratio),
  }));
}

/** Label shown at the right edge of a Fibonacci level, e.g. {@code "61.8% (152.40)"}. */
export function formatFibLabel(level) {
  if (!level || !isNum(level.price)) return '';
  return `${level.percentLabel} (${level.price.toFixed(2)})`;
}

/**
 * The slope of the line through two points, in price per bar.
 *
 * Returns null for a vertical line: two clicks on the same bar define no direction, and letting
 * that through would produce Infinity and an overlay that renders nowhere.
 */
export function slopeBetween(p1, p2) {
  if (!validPoint(p1) || !validPoint(p2)) return null;
  const dx = p2.index - p1.index;
  if (dx === 0) return null;
  return (p2.price - p1.price) / dx;
}

/**
 * Extends the line through P1 and P2 out to the x-position of P3.
 *
 * Only P3's X is used — its price is deliberately ignored, because the tool's purpose is to ask
 * "where does this line reach by then?", and honouring P3's price would just draw a line to
 * wherever the user happened to click.
 *
 * @returns {{index:number, price:number}|null} the projected end point
 */
export function extendedTrendlineEnd(p1, p2, p3) {
  const m = slopeBetween(p1, p2);
  if (m === null || !validPoint(p3)) return null;
  // b = y1 - m*x1, then y = m*x + b. Written as a delta from P1 to avoid the large intermediate
  // intercept value when indices are big.
  return { index: p3.index, price: p1.price + m * (p3.index - p1.index) };
}

/**
 * Andrews' pitchfork from three pivots.
 *
 * The median runs from P1 through the midpoint of P2–P3; the prongs start at P2 and P3 and run
 * parallel to it. All three are extended to {@code lastIndex} so the fork opens out across the
 * remaining chart, which is the whole point of the tool.
 *
 * The midpoint is kept FRACTIONAL for the slope calculation and only rounded when an endpoint has
 * to land on a bar. Rounding first would tilt the median by up to half a bar, and every prong
 * inherits that error since they are all drawn parallel to it.
 *
 * @returns {{median:object, upper:object, lower:object, base:object, midpoint:object}|null}
 */
export function pitchfork(p1, p2, p3, lastIndex) {
  if (!validPoint(p1) || !validPoint(p2) || !validPoint(p3)) return null;

  const midpoint = {
    index: (p2.index + p3.index) / 2,
    price: (p2.price + p3.price) / 2,
  };

  const m = slopeBetween(p1, midpoint);
  if (m === null) return null;   // P1 sits on the same bar as the midpoint: no direction

  const end = isNum(lastIndex) ? Math.max(lastIndex, p1.index, p2.index, p3.index) : null;
  if (end === null) return null;

  const ray = (from) => ({
    from: { index: from.index, price: from.price },
    to: { index: end, price: from.price + m * (end - from.index) },
  });

  return {
    slope: m,
    midpoint,
    median: ray(p1),
    upper: ray(p2),
    lower: ray(p3),
    // Closes the base of the fork so the three pivots read as one shape.
    base: { from: { ...p2 }, to: { ...p3 } },
  };
}

/**
 * The horizontal segments a Fibonacci retracement draws, ready to become overlays.
 *
 * Every segment — the 0% and 100% boundaries included — is built from the SAME pair of indices, so
 * they all span exactly the same width. That is the point of returning them from one function: the
 * boundaries were previously short caps around each click, drawn by separate code, which left them
 * visibly narrower than the levels they bracket. Two code paths computing "the same" span is how
 * they drift apart again.
 *
 * Style differs (boundaries solid and slightly heavier, interior dotted) but geometry does not.
 */
export function fibonacciSegments(p1, p2, { maxIndex = null } = {}) {
  const levels = fibonacciLevels(p1, p2);
  if (levels.length === 0) return [];

  // Normalised to the axis, not to click order. A retracement drawn right-to-left has its second
  // click on the LEFT, so extending `p2.index` would have pushed the lines further into the past —
  // and the label, which anchors to the segment's second point, would have sat at the left edge
  // pointing right, inside the box it is meant to annotate. Both ends are decided by position now,
  // which is the only reading under which "the right-hand endpoint" means anything.
  const leftIndex = Math.min(p1.index, p2.index);
  const rightIndex = Math.max(p1.index, p2.index);

  // The extension is a fraction of the MOVE, not a fixed number of bars.
  //
  // It was FIB_EXTENSION_BARS — a constant 10 — which is wrong in both directions and was visible
  // on a real chart: a retracement drawn across three bars got a ten-bar tail three times its own
  // width, and one drawn across four hundred got ten bars, which at that zoom is not a tail at all.
  // Half the span the user actually drew scales with the thing it extends, which is what "extend
  // the study" means when the study is two clicks wide.
  //
  // Rounded, because an index has to land on a bar. A move of one bar therefore extends by one
  // (Math.round(0.5) is 1 in JS), not zero — a one-bar tail on a one-bar move is proportionate,
  // and a zero-length extension would be indistinguishable from the feature not working.
  const span = rightIndex - leftIndex;
  const extendBy = Math.max(1, Math.round(span * FIB_EXTENSION_FRACTION));

  // Never shorter than the move that was drawn. Without the outer max, a retracement drawn beyond
  // the cap — possible, since the padded future bars are clickable — would come back with its two
  // ends crossed over and render as a line running backwards.
  const extended = rightIndex + extendBy;
  const endIndex = maxIndex == null ? extended : Math.max(rightIndex, Math.min(maxIndex, extended));

  return levels.map(level => ({
    part: `fib-${level.ratio}`,
    from: { index: leftIndex, price: level.price },
    to: { index: endIndex, price: level.price },
    isBoundary: level.isBoundary,
    lineType: level.isBoundary ? 'solid' : 'dotted',
    lineWidth: level.isBoundary ? 2 : 1,
    label: formatFibLabel(level),
    // Drawn to the LEFT of the level's right end, which puts it inside the plot area.
    //
    // Measured, not guessed: a tool label defaults to `position: 'right'` with an 8px gap, and
    // "61.8% (152.40)" is about 90px wide. The chart opens showing the whole history, so a bar
    // near the right end of the data is only a few pixels from the grid edge — 862 of 870 at 3,000
    // bars, 868 of 870 at 11,000 — and the whole label rendered on top of the price scale. The
    // extension moves the anchor further right, so it makes an existing problem certain rather
    // than likely.
    //
    // 'left' anchors the label's RIGHT edge at the level's end, so it tracks the extended edge and
    // can never cross it, at any zoom. The trade is that the text sits over the last few bars of
    // the level it names, which is empty future space here by construction.
    labelPosition: 'left',
  }));
}

/**
 * Bars of empty future workspace the chart shows past the last price bar by default.
 *
 * Lives here rather than in the chart component because it is the ceiling for everything below it:
 * a projection longer than this is computed, rendered, and invisible until the user pans. Keeping
 * the three numbers in one file is what lets a single test hold them in the right order — it was a
 * bare 50 inline in EChartComponent, which is how that relationship goes unnoticed.
 */
export const FUTURE_VIEW_BARS = 50;

/**
 * Bars a projection may run past the last real price bar.
 *
 * The chart pads ~250 future weekday slots, so there is space to the right; this is how much of it
 * a fork or projection is allowed to use. Enough to read where the rays are heading, without the
 * fork fanning so wide across empty space that the part near the pivots is squeezed flat.
 */
export const FUTURE_PROJECTION_BARS = 20;

/**
 * How far past the move a Fibonacci level runs, as a fraction of the move's own width.
 *
 * A retracement is read forwards — the levels matter for where price goes next, not for where it
 * has already been — but the lines used to stop dead on the second click, so the levels ran out
 * exactly where they started being useful. They now carry on into the future workspace.
 *
 * <p><b>Proportional, not a fixed bar count.</b> This was half of FUTURE_PROJECTION_BARS, a flat 10
 * bars, and it read wrong at both ends of the zoom range: a three-bar retracement grew a tail three
 * times its own width, and a four-hundred-bar one grew a tail too short to see. Half of what the
 * user drew scales with it.
 *
 * <p>The forward budget still applies as a CAP — see the {@code maxIndex} argument — so a move
 * eight hundred bars wide does not throw a four-hundred-bar tail across the future workspace and
 * stack its labels against the price axis.
 */
export const FIB_EXTENSION_FRACTION = 0.5;

/**
 * Bars a Fibonacci level runs past the right-hand end of a move of {@code span} bars.
 *
 * <p>Exported as a function rather than a constant because the extension is proportional: it is
 * half the width of what was drawn, so it has no value until there is a move to measure.
 */
export function fibExtensionBars(span) {
  if (!Number.isFinite(span)) return 0;
  return Math.max(1, Math.round(Math.abs(span) * FIB_EXTENSION_FRACTION));
}

/** How many clicks each tool needs before it draws. */
export const TOOL_CLICKS = {
  draw_fibonacci: 2,
  draw_fib_time: 2,
  draw_extended_trendline: 3,
  draw_pitchfork: 3,
};
